import type { TourTraveller } from "@getaways/core";
import { DatePicker, Field, optionsOf, Select } from "@getaways/extranet-ui";
import { TITLES } from "./labels";

/** A traveller being typed: dates and passport may still be empty. */
export type Draft = Omit<TourTraveller, "passport" | "emergencyContact" | "gender"> & { gender: NonNullable<TourTraveller["gender"]> | ""; passportNumber: string; passportExpiry: string };

export const blankTraveller = (): Draft => ({ title: "MS", firstName: "", lastName: "", dateOfBirth: "", gender: "", nationality: "PT", email: "", phone: "", passportNumber: "", passportExpiry: "" });

export function fromTraveller(t: TourTraveller): Draft {
  return { ...t, gender: t.gender ?? "", phone: t.phone ?? "", passportNumber: t.passport?.number ?? "", passportExpiry: t.passport?.expiry ?? "" };
}

/** The traveller as the API takes it: empty fields left out, the passport only when both parts are there. */
export function toTraveller(d: Draft): TourTraveller {
  const { passportNumber, passportExpiry, gender, phone, middleName, ...rest } = d;
  return {
    ...rest,
    ...(middleName ? { middleName } : {}),
    ...(gender ? { gender } : {}),
    ...(phone ? { phone } : {}),
    ...(passportNumber && passportExpiry ? { passport: { number: passportNumber, expiry: passportExpiry } } : {}),
  };
}

/** Legal names as in the passport, birth date and nationality: what tour operators need to hold a space. */
export function TravellerFields({ value, onChange, problems = {} }: { value: Draft; onChange: (patch: Partial<Draft>) => void; problems?: Record<string, string> }) {
  return (
    <div className="grid2">
      <Field label="Title"><Select value={value.title} onChange={(title) => onChange({ title })} options={optionsOf<TourTraveller["title"]>(TITLES)} /></Field>
      <Field label="First name" help="As in the passport"><input value={value.firstName} onChange={(e) => onChange({ firstName: e.target.value })} /></Field>
      <Field label="Last name"><input value={value.lastName} onChange={(e) => onChange({ lastName: e.target.value })} /></Field>
      <Field label="Date of birth" error={problems.dateOfBirth}><DatePicker value={value.dateOfBirth} onChange={(dateOfBirth) => onChange({ dateOfBirth })} /></Field>
      <Field label="Gender"><Select<Draft["gender"]> value={value.gender} onChange={(gender) => onChange({ gender })} options={[{ value: "", label: "Not given" }, { value: "F", label: "Female" }, { value: "M", label: "Male" }, { value: "X", label: "Other" }]} /></Field>
      <Field label="Nationality" help="ISO code, e.g. PT"><input maxLength={2} value={value.nationality} onChange={(e) => onChange({ nationality: e.target.value.toUpperCase() })} /></Field>
      <Field label="Email"><input type="email" value={value.email} onChange={(e) => onChange({ email: e.target.value })} /></Field>
      <Field label="Phone"><input value={value.phone ?? ""} onChange={(e) => onChange({ phone: e.target.value })} placeholder="+351…" /></Field>
      <Field label="Passport number" error={problems.passport} help="Can come later, before travel"><input value={value.passportNumber} onChange={(e) => onChange({ passportNumber: e.target.value })} /></Field>
      <Field label="Passport expiry"><DatePicker value={value.passportExpiry} onChange={(passportExpiry) => onChange({ passportExpiry })} /></Field>
    </div>
  );
}
